<template>
  <!--
    水墨打字文本（打字机模糊 → 瞬间对焦）
    用法：
      <ink-text text="技能库已就绪" />
      <ink-text text="加载完成" :stagger="30" :duration="450" />
    限制：仅用于 ≤20 字的短文本（标题、状态文案、空态提示），
          长文本会因逐字动画影响可读性，组件内部会自动降级为直接显示。
  -->
  <span
    v-if="isLong"
    class="ink-text ink-text--plain"
  >{{ text }}</span>
  <span
    v-else
    class="ink-text"
    :style="containerStyle"
  >
    <span
      v-for="(char, index) in chars"
      :key="index"
      class="ink-text__char"
      :style="charStyle(index)"
    >{{ char }}</span>
  </span>
</template>

<script>
// MAX_CHARS 是逐字动画的字数上限：超过后禁用动画，避免长文本可读性下降。
// 12 字以内：完整水墨晕染（错开 30ms 的波浪感最明显）；
// 13-20 字：过渡区间，缩短错开时长、加快单字对焦，避免「一行字拖两秒」；
// 20 字以上：直接静态呈现（isLong），不再逐字，长段落保持可读。
const MAX_CHARS = 20
const DENSE_FROM = 12

export default {
  name: 'InkText',
  props: {
    // text 是需要展示的短文本。
    text: { type: String, default: '' },
    // stagger 是相邻字的错开时间（毫秒），建议 20-40。
    stagger: { type: Number, default: 30 },
    // duration 是单个字从模糊到对焦的时长（毫秒）。
    duration: { type: Number, default: 450 },
    // tag 允许指定语义化标签，默认 span。
    tag: { type: String, default: 'span' }
  },
  data() {
    return {
      // reduceMotion 记录用户是否偏好减少动态效果（JS 侧降级，兼容不支持媒体查询的老环境）。
      reduceMotion: false
    }
  },
  computed: {
    // chars 把文本拆成单字数组，保留空格。
    chars() {
      return Array.from(this.text || '')
    },
    // isLong 判断是否超过逐字动画上限。
    isLong() {
      return this.chars.length > MAX_CHARS
    },
    // containerStyle 把错开时长与单字时长下发为 CSS 变量。
    // 文字变长时按比例压缩节奏：错开时长最低压到 12ms、单字对焦压到 260ms，
    // 保证 13-20 字这一档的整体时长不会长到让人等。
    containerStyle() {
      const len = this.chars.length
      let stagger = this.stagger
      let duration = this.duration
      if (len > DENSE_FROM) {
        const ratio = Math.min(1, (len - DENSE_FROM) / (MAX_CHARS - DENSE_FROM))
        stagger = Math.max(12, Math.round(this.stagger * (1 - ratio * 0.6)))
        duration = Math.max(260, Math.round(this.duration * (1 - ratio * 0.42)))
      }
      return {
        '--ink-stagger': `${stagger}ms`,
        '--ink-duration': `${duration}ms`
      }
    }
  },
  created() {
    // 初始化时读取一次媒体查询，SSR 或老浏览器下安全兜底为 false。
    if (typeof window !== 'undefined' && window.matchMedia) {
      this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches
    }
  },
  methods: {
    // charStyle 给每个字注入序号，用于计算 animation-delay。
    charStyle(index) {
      if (this.reduceMotion) {
        return { animation: 'none', opacity: 1, filter: 'none' }
      }
      return {
        '--ink-index': index,
        animationDuration: `${this.duration}ms`
      }
    }
  }
}
</script>

<style scoped lang="scss">
// 容器：逐字动画的定位上下文，单独提升合成层避免整页重绘。
.ink-text {
  display: inline-block;
  white-space: nowrap;
  will-change: filter, opacity;
  transform: translateZ(0);
}

// 长文本降级：不拆字、不做动画，保证可读性。
.ink-text--plain {
  white-space: normal;
}

// 单个字：模糊 + 上浮 → 清晰归位。
.ink-text__char {
  display: inline-block;
  opacity: 0;
  filter: blur(8px);
  transform: translateY(2px);
  animation-name: ink-focus-in;
  animation-timing-function: var(--ease-out-soft, cubic-bezier(0.25, 0.1, 0.25, 1));
  animation-fill-mode: forwards;
  // 相邻字错开：第 n 个字延迟 n × stagger。
  animation-delay: calc(var(--ink-index, 0) * var(--ink-stagger, 30ms));
}

@keyframes ink-focus-in {
  to {
    opacity: 1;
    filter: blur(0);
    transform: translateY(0);
  }
}

// 无障碍兜底：系统偏好减少动态效果时直接显示。
@media (prefers-reduced-motion: reduce) {
  .ink-text__char {
    opacity: 1;
    filter: none;
    transform: none;
    animation: none;
  }
}
</style>
